import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryStatusApi } from '../services/inventoryStatusApi';
import type { InventoryStatus } from '../types/boat.types';

export const statusKeys = {
  all: ['inventory-statuses'] as const,
  lists: () => [...statusKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...statusKeys.lists(), filters] as const,
  details: () => [...statusKeys.all, 'detail'] as const,
  detail: (id: string) => [...statusKeys.details(), id] as const,
};

export function useStatuses(filters?: Record<string, any>) {
  return useQuery({
    queryKey: statusKeys.list(filters || {}),
    queryFn: () => inventoryStatusApi.getStatuses(filters),
  });
}

export function useStatus(id: string) {
  return useQuery({
    queryKey: statusKeys.detail(id),
    queryFn: () => inventoryStatusApi.getStatus(id),
    enabled: !!id,
  });
}

export function useCreateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Partial<InventoryStatus>) =>
      inventoryStatusApi.createStatus(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.lists() });
    },
  });
}

export function useUpdateStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<InventoryStatus> }) =>
      inventoryStatusApi.updateStatus(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: statusKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: statusKeys.lists() });
    },
  });
}

export function useDeleteStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => inventoryStatusApi.deleteStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: statusKeys.lists() });
    },
  });
}
